<script setup>
import { ref, watch, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { RoadmapClient } from "@/services/RoadmapClient";
import { useStore } from "vuex";

const store = useStore();
const isAuthenticated = computed(() => store.state.isAuthenticated);

const router = useRouter();
const roadmapClient = new RoadmapClient();
const route = useRoute();

const props = defineProps({
  chapterId: {
    type: [String, Number],
    required: true,
  },
  courseId: {
    type: [String, Number],
    required: true,
  },
});

const roadmap = ref([]);
const loading = ref(false);
const error = ref(null);

// ✅ Combined content now comes only from roadmap
const combinedContent = computed(() => {
  const chapterData = roadmap.value.find(
    (chap) => chap.capitulo_id === Number(props.chapterId)
  );
  if (!chapterData) return [];

  return chapterData.unidades
    .map((unit) => ({
      id: unit.ref_id,
      unidadId: unit.unidad_id,
      type: unit.tipo === "leccion" ? "lesson" : "quiz",
      order: unit.orden,
      titulo: unit.titulo, // ✅ direct from API now
      isLocked: unit.locked,
      reason: unit.reason,
      details: unit.details,
      isCompleted: unit.completed,
    }))
    .sort((a, b) => a.order - b.order);
});

const fetchRoadmap = async () => {
  try {
    loading.value = true;
    error.value = null;

    const roadmapResponse = await roadmapClient.getRoadmap(props.courseId);
    roadmap.value = roadmapResponse;

    console.log("roadmap", roadmap.value);
  } catch (err) {
    error.value = err.message || "Failed to load roadmap data";
    console.error("Error loading roadmap:", err);
  } finally {
    loading.value = false;
  }
};

const goToContent = (item) => {
  if (item.type === "lesson") {
    router.push({
      path: `/cursos/${props.courseId}/${props.chapterId}/${item.id}`,
    });
  } else {
    router.push({
      path: `/cursos/${props.courseId}/${props.chapterId}/quiz/${item.id}`,
    });
  }
};

const formatRequirement = (item) => {
  if (!item.isLocked) return "";

  // Quiz requirement
  if (item.reason === "QUIZ_BLOCKER_NOT_PASSED" && item.details) {
    return `Debes aprobar "<strong>${
      item.details.quiz_title
    }</strong>" para acceder a este contenido <br> 
            Nota mínima: <strong>${item.details.need}</strong> <br>
            Mejor intento: <strong>${item.details.best ?? "N/A"}</strong>`;
  }

  // Chapter requirement
  if (item.reason === "CHAPTER_PREREQ_NOT_MET" && Array.isArray(item.details)) {
    return item.details
      .map((d) => `"Capítulo ${d.id}" debe estar ${d.rule.toLowerCase()}`)
      .join("<br>");
  }

  return "Requiere aprobar contenido previo";
};

const isCurrentContent = (item) =>
  item.id == (item.type === "quiz" ? route.params.quiz : route.params.leccion);

// Watch for changes
watch(
  () => props.chapterId,
  (newVal) => {
    if (newVal) fetchRoadmap();
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <!-- Loading and error states remain the same -->
    <div v-if="loading" class="text-center py-3">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      Error loading chapter: {{ error }}
    </div>

    <div v-else>
      <!-- Combined Content Section -->
      <div class="card mt-4 mt-md-0" v-if="combinedContent?.length">
        <div class="card-header p-3">
          <h6 class="mb-0">Contenido del Capítulo</h6>
        </div>
        <div class="card-body p-0">
          <div class="accordion" id="contentAccordion">
            <div
              v-for="(item, index) in combinedContent"
              :key="item.id"
              class="accordion-item"
              :class="{ 'current-content': isCurrentContent(item) }"
            >
              <h2 class="accordion-header">
                <button
                  class="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="'#contentCollapse' + index"
                  :class="{ 'bg-light': isCurrentContent(item) }"
                >
                  <div class="d-flex align-items-center w-100">
                    <i
                      :class="[
                        item.type === 'lesson'
                          ? 'fas fa-book me-3 fa-xs text-primary'
                          : 'fas fa-question-circle fa-xs me-3 text-success',
                      ]"
                    ></i>
                    <h6 class="me-auto text-md mb-0">{{ item.titulo }}</h6>
                    <i
                      v-if="item.isLocked"
                      class="fas fa-lock fa-xs text-sm text-secondary ms-2"
                    ></i>
                    <i
                      v-if="item.isCompleted"
                      class="fas fa-check-circle fa-xs text-sm text-primary ms-2"
                    ></i>
                  </div>
                </button>
              </h2>

              <div
                :id="'contentCollapse' + index"
                class="accordion-collapse collapse"
                data-bs-parent="#contentAccordion"
                :class="{ show: isCurrentContent(item) }"
              >
                <div class="accordion-body">
                  <!-- Locked Content -->
                  <div v-if="item.isLocked" class="mb-3">
                    <h6 class="fw-bold">Acceso restringido</h6>
                    <small>
                      <i class="fas fa-exclamation-circle me-2"></i>
                      <span v-html="formatRequirement(item)"></span>
                    </small>
                  </div>

                  <!-- Lesson Content -->
                  <div
                    v-else-if="item.type === 'lesson'"
                    class="lesson-descripcion mb-3"
                  >
                    <h6 class="fw-bold">Instrucciones:</h6>
                    <div
                      v-if="item.contenido_texto"
                      v-html="item.contenido_texto"
                    ></div>
                    <div v-else>
                      <p class="text-muted">
                        No hay instrucciones para esta lección.
                      </p>
                    </div>
                    <button
                      class="btn btn-dark mt-3"
                      :disabled="!isAuthenticated || item.isLocked"
                      @click="goToContent(item)"
                    >
                      <i class="fas fa-play me-2"></i>Ir a lección
                    </button>
                  </div>

                  <!-- Quiz Content -->
                  <div
                    v-else-if="item.type === 'quiz'"
                    class="quiz-instructions mb-3"
                  >
                    <h6 class="fw-bold">Instrucciones:</h6>
                    <div
                      v-if="item.instrucciones"
                      v-html="item.instrucciones"
                    ></div>
                    <div v-else>
                      <p class="text-muted">
                        No hay instrucciones para este quiz.
                      </p>
                    </div>
                    <button
                      class="btn btn-success mt-3"
                      :disabled="!isAuthenticated || item.isLocked"
                      @click="goToContent(item)"
                    >
                      <i class="fas fa-pencil-alt me-2"></i>Ir a Quiz
                    </button>
                  </div>

                  <!-- Authentication Notice -->
                  <div
                    v-if="!isAuthenticated"
                    class="text-muted bg-gray-100 rounded p-2 mb-2 small"
                  >
                    <i class="fas fa-info-circle pe-2"></i>Debes
                    <router-link
                      to="/autenticacion/iniciar-sesion"
                      class="fw-bold text-info text-decoration-none"
                      >Iniciar Sesión</router-link
                    >
                    o
                    <router-link
                      to="/autenticacion/registro"
                      class="fw-bold text-info text-decoration-none"
                      >Registrarte</router-link
                    >
                    para ver este contenido.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-4">
        <p class="text-muted">Este capítulo no tiene contenido aún.</p>
      </div>
    </div>
  </div>
</template>


<style>
.quiz-instructions {
  background-color: #f8f9fa;
  border-left: 4px solid #0dfd69;
  padding: 0.75rem 1rem;
  border-radius: 0.25rem;
}

.lesson-descripcion {
  background-color: #f8f9fa;
  border-left: 4px solid #102d58;
  padding: 0.75rem 1rem;
  border-radius: 0.25rem;
}

.accordion-item .form-check-input:checked ~ .accordion-button .fa-book {
  color: #6c757d !important;
  /* Gray color for completed */
}

.accordion-item .form-check-input:checked ~ .accordion-button h6 {
  text-decoration: line-through;
  color: #6c757d;
}

.requirement-text {
  white-space: pre-line;
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.lesson-content,
.lesson-descripcion {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.5rem;
}

.lesson-descripcion {
  background-color: #f8f9fa;
  border-left: 4px solid #102d58;
  padding: 0.4rem 1rem;
  border-radius: 0.25rem;
  min-height: 100px;
}

.quiz-instructions {
  border-left: 4px solid #0dfd69;
}

.sticky-top {
  position: sticky;
  top: 1rem;
}

.accordion-item {
  border-bottom: 1px solid #dee2e6;
}

.accordion-button:not(.collapsed) {
  background-color: #f8f9fa;
}

.dropdown-menu {
  max-height: 300px;
  overflow-y: auto;
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.bg-light {
  background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
}

.resources-section {
  background-color: #f8f9fa;
  border-left: 4px solid #102d58;
  padding: 0.4rem 1rem;
  border-radius: 0.25rem;
  min-height: 150px;
}

.resources-list {
  border-radius: 0.5rem;
  overflow: hidden;
}

.list-group-item {
  transition: all 0.2s ease;
  border-left: none;
  border-right: none;
}

.list-group-item:first-child {
  border-top: none;
}

.list-group-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.text-muted {
  font-size: 0.8rem;
}

.custom-hover-underline:hover h6 {
  text-decoration: underline;
}
</style>